import { type SVGProps } from 'react';

import { cn } from '@/app/shared/ui';

export interface TenkoLogoProps extends Omit<SVGProps<SVGSVGElement>, 'fill'> {
  /** Renders the icon mark only. Default `true`. Set to `false` to also render the "Tenko" wordmark next to it. */
  iconOnly?: boolean;
  /** Accepted for `BrandLogoProps` parity; the Tenko mark is single-color (`currentColor`) in both variants. */
  variant?: 'mono' | 'color';
  /** Wordmark size when `iconOnly` is `false`. */
  wordmarkClassName?: string;
}

/** Tenko brand mark (`fill: currentColor`) with an optional wordmark. */
export const TenkoLogo = ({
  iconOnly = true,
  variant: _variant,
  className,
  wordmarkClassName,
  ...svgProps
}: TenkoLogoProps) => {
  const svg = (
    <svg
      viewBox="0 0 323 284"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={cn('shrink-0', className)}
      {...svgProps}
    >
      <path d="M0 167.196V20.1954C0.000413062 2.23836 21.7434 -6.7532 34.4607 5.94415L83.4509 54.8591C84.3973 55.8041 85.6856 56.3354 87.0241 56.3354H262.438C295.885 56.3354 323 83.4085 323 116.805V263.806C322.998 281.761 301.256 290.753 288.539 278.057L239.549 229.142C238.603 228.197 237.319 227.666 235.981 227.666H60.5625C27.115 227.666 0.000400846 200.592 0 167.196ZM20.1875 167.196C20.1879 189.46 38.2643 207.509 60.5625 207.509H235.981C242.672 207.51 249.091 210.162 253.822 214.886L302.812 263.806V116.805C302.812 94.5406 284.736 76.4919 262.438 76.4919H87.0241C80.3315 76.4919 73.91 73.8355 69.1777 69.1104L20.1875 20.1954V167.196Z" />
      <path d="M246.539 236.477L66 66L258.854 66.8796C259.663 66.8833 260.468 66.9843 261.253 67.1804L278.541 71.5016C279.662 71.7818 280.726 72.2526 281.688 72.8935L294.527 81.4511C295.079 81.8193 295.594 82.2412 296.063 82.7105L305.985 92.6303C307.277 93.9221 308.193 95.5407 308.636 97.313L312.699 113.56C312.899 114.36 313 115.181 313 116.005V244.803C313 245.466 312.934 246.128 312.804 246.779L308.88 266.399C308.344 269.074 305.995 271 303.266 271C301.461 271 299.762 270.149 298.681 268.703L296.048 265.181C295.19 264.034 294.098 263.082 292.844 262.39L248.59 237.974C247.846 237.563 247.157 237.06 246.539 236.477Z" />
    </svg>
  );

  if (iconOnly) return svg;

  return (
    <span
      className="inline-flex items-center gap-2"
      aria-label="Tenko"
      role="img"
    >
      {svg}
      <span
        className={cn(
          'font-outfit font-semibold text-lg tracking-tight leading-none',
          wordmarkClassName,
        )}
      >
        Tenko
      </span>
    </span>
  );
};
